import autoTable from "jspdf-autotable";
import {
  fontFamily,
  capitalizeWords,
  getProportionalDimensions,
} from "./utils.js";
import { addHeader, addFooter } from "./headerFooter.js";
import { fetchImageUrl, convertToBase64 } from "./imageService.js";

// Modificar la función renderChecklistSection para manejar imágenes y layout alternativo
export const renderChecklistSection = async (doc, seccion, report, y) => {
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(8);
  doc.text(
    "CÓDIGOS EMPLEADOS: OK - CORRECTO  NG - ESTÁ MAL  NA - NO APLICA  ND - NO DISPONIBLE",
    doc.internal.pageSize.getWidth() / 2,
    y,
    { align: "center" },
  );
  y += 6;

  // ✅ Detectar si se debe usar el layout alternativo (igual que en ChecklistSection.jsx)
  const numMaquinas = seccion.maquinas ? seccion.maquinas.length : 0;
  const numItems = seccion.items ? seccion.items.length : 0;
  const useRowLayout = numMaquinas < 3 && numItems > 8;

  console.log(
    `PDF Layout - Máquinas: ${numMaquinas}, Items: ${numItems}, useRowLayout: ${useRowLayout}`,
  );

  if (useRowLayout) {
    // ✅ Layout alternativo: items en filas, máquinas en columnas
    return await renderChecklistRowLayout(doc, seccion, report, y);
  } else {
    // ✅ Layout normal: máquinas en filas, items en columnas
    return await renderChecklistNormalLayout(doc, seccion, report, y);
  }
};

// ✅ Función para renderizar checklist en layout normal (máquinas en filas, items en columnas)
export const renderChecklistNormalLayout = async (doc, seccion, report, y) => {
  const maxWidth = doc.internal.pageSize.getWidth() - 30; // Ancho disponible en mm

  // Mapear encabezados
  const headers = seccion.items.map((item) =>
    doc.splitTextToSize(item.descripcion, maxWidth),
  );

  // Almacenar referencias a las celdas que contienen imágenes
  const imageCells = [];

  // Cargar todas las imágenes necesarias antes de dibujar la tabla
  const imagePromises = [];
  const imageCache = {};

  // Agrupar máquinas por ubicación (mayúsculas)
  const maquinasPorUbicacion = {};
  seccion.maquinas.forEach((maquina) => {
    const ubicacionRaw = maquina.ubicacion || "Sin ubicación";
    const ubicacion = ubicacionRaw.toUpperCase();
    if (!maquinasPorUbicacion[ubicacion]) maquinasPorUbicacion[ubicacion] = [];
    maquinasPorUbicacion[ubicacion].push(maquina);
  });

  // Construir filas: por cada grupo de ubicación, agregar fila de ubicación y luego las máquinas
  const rows = [];
  let rowIndex = 0;
  const tieneColumnaNumero = seccion.items.some((item) =>
    item.descripcion.trim().startsWith("#") ||
    report?.atributosMaquina?.some((attr) =>
      attr.idatributomaquina === item.idatributomaquina && attr.keyatributo === "numero",
    ),
  );
  Object.entries(maquinasPorUbicacion).forEach(([ubicacion, maquinasGrupo]) => {
    if (tieneColumnaNumero) {
      maquinasGrupo.sort((a, b) => {
        const numeroA = String(a.numero ?? "").trim();
        const numeroB = String(b.numero ?? "").trim();
        return Number(!numeroA) - Number(!numeroB) ||
          numeroA.localeCompare(numeroB, undefined, { numeric: true, sensitivity: "base" });
      });
    }
    // Fila de ubicación (array plano, con colSpan y marca especial)
    const ubicacionRow = new Array(headers.length).fill("");
    ubicacionRow[0] = {
      content: ubicacion,
      colSpan: headers.length,
      _isUbicacion: true,
    };
    rows.push(ubicacionRow);
    rowIndex++;
    // Filas de máquinas de esa ubicación
    maquinasGrupo.forEach((maquina) => {
      const row = seccion.items.map((headerItem, colIndex) => {
        const maquinaItem = maquina.items.find(
          (item) => item.id === headerItem.id,
        );
        if (!maquinaItem) return "-";

        if (maquinaItem.idtipoitem === 1) {
          switch (maquinaItem.idtiporespuesta) {
            case 1: // Booleano
              // Si tiene valor precargado, mostrar directamente la respuesta precargada
              if (maquinaItem.tienePrecarga && maquinaItem.valorPrecargado) {
                return maquinaItem.valorPrecargado;
              }
              if (maquinaItem.respuesta === null) return "N/A";
              return maquinaItem.respuesta ? "OK" : "N/G";
            case 2: // Numérico
              return maquinaItem.respuesta || "-";
            case 3: // Texto
              return maquinaItem.respuesta || "-";
            case 4: // Imagen
              try {
                if (maquinaItem.respuesta) {
                  let imagePath, description;
                  if (typeof maquinaItem.respuesta === "string") {
                    try {
                      const imageData = JSON.parse(maquinaItem.respuesta);
                      imagePath = imageData.valorimagen || imageData.path;
                      description =
                        imageData.descripcion || imageData.description || "";
                    } catch (e) {
                      imagePath = maquinaItem.respuesta;
                      description = "";
                    }
                  } else if (
                    typeof maquinaItem.respuesta === "object" &&
                    maquinaItem.respuesta !== null
                  ) {
                    imagePath =
                      maquinaItem.respuesta.valorimagen ||
                      maquinaItem.respuesta.path;
                    description =
                      maquinaItem.respuesta.descripcion ||
                      maquinaItem.respuesta.description ||
                      "";
                  }
                  if (imagePath) {
                    if (imagePath.startsWith('"') && imagePath.endsWith('"')) {
                      imagePath = imagePath.slice(1, -1);
                    }
                    // Registrar esta celda para agregar la imagen después
                    imageCells.push({
                      rowIndex,
                      colIndex,
                      imagePath,
                      description,
                    });
                    // Cargar la imagen si no está en caché
                    if (!imageCache[imagePath]) {
                      const promise = (async () => {
                        const imageUrl = await fetchImageUrl(imagePath);
                        if (imageUrl) {
                          imageCache[imagePath] =
                            await convertToBase64(imageUrl);
                        }
                        return imageCache[imagePath];
                      })();
                      imagePromises.push(promise);
                    }
                    return "[Imagen]";
                  }
                }
                return "Sin imagen";
              } catch (error) {
                console.error("Error procesando imagen:", error);
                return "Error imagen";
              }
            default:
              return "-";
          }
        } else if (maquinaItem.idtipoitem === 2) {
          // Replicar lógica de ChecklistSection.jsx para mostrar datos de consulta de la máquina
          const columnToAttributeMap = {
            "#": "numero",
            "# VÁLVULA": "numero",
            "# Válvula": "numero",
            Ubicación: "ubicacion",
            UBICACIÓN: "ubicacion",
            Tipo: "tipo",
            TIPO: "tipo",
            "Área Protegida": "areaprotegida",
            "ÁREA PROTEGIDA": "areaprotegida",
            Módulo: "modulo",
            MÓDULO: "modulo",
            Diámetro: "diametro",
            DIÁMETRO: "diametro",
            "Medida Nominal": "medidanominal",
            "MEDIDA NOMINAL": "medidanominal",
            "Matriz Causa y Efecto": "matrizcausaefecto",
            "MATRIZ CAUSA Y EFECTO": "matrizcausaefecto",
            Descripción: "descripcion",
            DESCRIPCIÓN: "descripcion",
            Descripcion: "descripcion",
            OBSERVACIONES: "observaciones",
            Observaciones: "observaciones",
          };

          // Normalizar la descripción del item para buscar en el mapa
          const descripcionNormalizada = maquinaItem.descripcion.trim();

          if (descripcionNormalizada.startsWith("#")) {
            return String(maquina.numero ?? "-");
          }

          // Caso especial para Ubicación: mostrar ubicacionespecifica
          if (
            descripcionNormalizada === "Ubicación" ||
            descripcionNormalizada === "UBICACIÓN" ||
            descripcionNormalizada === "Ubicacion"
          ) {
            return maquina.ubicacionespecifica || maquina.ubicacion || "-";
          }

          // Caso especial para EQUIPO / UBICACIÓN: prioridad nombretipo (de idtipomaquina), fallback tipo (tipoespecifico)
          if (
            descripcionNormalizada === "EQUIPO / UBICACIÓN" ||
            descripcionNormalizada === "EQUIPO / UBICACION" ||
            descripcionNormalizada === "Equipo / Ubicación" ||
            descripcionNormalizada === "Equipo / Ubicacion"
          ) {
            // idmaquina 456 usa tipoespecifico directamente
            const equipo =
              maquina.idmaquina === 456
                ? maquina.tipo || ""
                : maquina.nombretipo || maquina.tipo || "";
            const ubic = maquina.ubicacion || "";
            const valor = [equipo, ubic].filter(Boolean).join(" / ");
            return valor || "-";
          }

          const atributoConfigurado = report?.atributosMaquina?.find(
            (attr) => attr.idatributomaquina === maquinaItem.idatributomaquina,
          );
          if (atributoConfigurado?.keyatributo && maquina[atributoConfigurado.keyatributo] !== undefined) {
            return String(maquina[atributoConfigurado.keyatributo] ?? "-");
          }

          // Buscar el atributo correspondiente en el mapa
          const atributo = columnToAttributeMap[descripcionNormalizada];

          if (atributo && maquina[atributo]) {
            // Si el atributo existe en la máquina, retornarlo
            const valor = maquina[atributo];
            return typeof valor === "string"
              ? capitalizeWords(valor)
              : String(valor);
          }

          // Si no se encuentra en el mapa directo, intentar buscar case-insensitive
          const atributoKey = Object.keys(columnToAttributeMap).find(
            (key) => key.toLowerCase() === descripcionNormalizada.toLowerCase(),
          );

          if (atributoKey) {
            const attr = columnToAttributeMap[atributoKey];
            if (maquina[attr]) {
              const valor = maquina[attr];
              return typeof valor === "string"
                ? capitalizeWords(valor)
                : String(valor);
            }
          }

          // Si nada funciona, retornar guion
          return "-";
        } else {
          return "Sin resultado";
        }
      });
      rows.push(row);
      rowIndex++;
    });
  });

  // Esperar a que todas las imágenes se carguen
  await Promise.all(imagePromises);

  // Definir dimensiones máximas para las imágenes en el checklist
  const maxImageWidth = 20; // mm
  const maxImageHeight = 20; // mm

  // Calcular dimensiones proporcionales para cada celda de imagen
  for (const cell of imageCells) {
    if (imageCache[cell.imagePath]) {
      cell.dimensions = await getProportionalDimensions(
        imageCache[cell.imagePath],
        maxImageWidth,
        maxImageHeight,
      );
    }
  }

  // Precalcular las alturas de las filas según las imágenes que contienen
  const rowHeights = {};
  for (const cell of imageCells) {
    if (imageCache[cell.imagePath]) {
      const { height: imgHeight } = cell.dimensions || {
        height: maxImageHeight,
      };
      // Añadir padding para descripción y márgenes
      const totalCellHeight = imgHeight + (cell.description ? 15 : 5);

      if (
        !rowHeights[cell.rowIndex] ||
        totalCellHeight > rowHeights[cell.rowIndex]
      ) {
        rowHeights[cell.rowIndex] = totalCellHeight;
      }
    }
  }

  // Renderizar la tabla con jsPDF autoTable

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: y,
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2,
      halign: "center",
      valign: "middle",
      lineColor: [0, 0, 0], // color negro para los bordes
      lineWidth: 0.1, // grosor de los bordes
    },
    headStyles: {
      fillColor: [204, 0, 0],
      textColor: [255, 255, 255],
      fontSize: 8,
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      lineColor: [0, 0, 0],
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
      lineColor: [0, 0, 0],
    },
    margin: { top: 50, bottom: 25 },
    didParseCell: function (data) {
      // Convertir todo el texto de la celda a MAYÚSCULAS
      if (data.cell && Array.isArray(data.cell.text)) {
        data.cell.text = data.cell.text.map((t) =>
          (typeof t === "string" ? t : String(t)).toUpperCase(),
        );
      }

      // Fila de ubicación: aplicar estilos especiales
      const cell = data.cell && data.cell.raw;
      if (cell && cell._isUbicacion) {
        // Evitar que autotable pinte el fondo por defecto (pintamos nosotros en willDrawCell)
        data.cell.styles.fillColor = false;
        // Estilos de texto
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.halign = "center";
        data.cell.styles.valign = "middle";
      }

      // Altura personalizada para filas con imágenes
      if (data.section === "body" && rowHeights[data.row.index]) {
        data.row.height = Math.max(data.row.height, rowHeights[data.row.index]);
      }
    },

    // Pinta el rectángulo semitransparente ANTES de que se dibuje el contenido de la celda
    willDrawCell: function (data) {
      const cellRaw = data.cell && data.cell.raw;
      if (!(cellRaw && cellRaw._isUbicacion)) return;

      const x = data.cell.x;
      const y = data.cell.y;
      const w = data.cell.width;
      const h = data.cell.height;

      const r = 204,
        g = 0,
        b = 0;
      const opacity = 0.75; // ajusta entre 0.0 y 1.0 según prefieras

      try {
        // Principal: usar GState si la versión de jsPDF lo soporta
        if (
          typeof doc.saveGraphicsState === "function" &&
          typeof doc.setGState === "function" &&
          typeof doc.GState === "function"
        ) {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: opacity }));
          doc.setFillColor(r, g, b);
          doc.rect(x, y, w, h, "F");
          doc.restoreGraphicsState();
        } else if (typeof doc.setAlpha === "function") {
          // Algunas builds expone setAlpha como alternativa
          doc.setFillColor(r, g, b);
          doc.setAlpha(opacity);
          doc.rect(x, y, w, h, "F");
          doc.setAlpha(1);
        } else {
          // Fallback: color más claro (simula baja opacidad)
          doc.setFillColor(204, 22, 22);
          doc.rect(x, y, w, h, "F");
        }
      } catch (err) {
        // Fallback por seguridad si alguna API lanza error
        try {
          doc.setFillColor(255, 153, 153);
          doc.rect(x, y, w, h, "F");
        } catch (e) {
          console.error("No se pudo pintar fondo semitransparente:", e);
        }
      }
    },

    didDrawCell: (data) => {
      // Dibujar la imagen en la celda utilizando dimensiones proporcionales
      for (const cell of imageCells) {
        if (
          data.row.index === cell.rowIndex &&
          data.column.index === cell.colIndex &&
          data.section === "body"
        ) {
          const imageBase64 = imageCache[cell.imagePath];
          if (imageBase64) {
            const { width: imgWidth, height: imgHeight } = cell.dimensions || {
              width: maxImageWidth,
              height: maxImageHeight,
            };
            const imgX = data.cell.x + (data.cell.width - imgWidth) / 2;
            const imgY = data.cell.y + 2;
            doc.addImage(imageBase64, "JPEG", imgX, imgY, imgWidth, imgHeight);
            if (cell.description) {
              const descriptionText =
                cell.description.length > 15
                  ? cell.description.substring(0, 15) + "..."
                  : cell.description;
              doc.setFontSize(7);
              doc.text(
                descriptionText,
                data.cell.x + data.cell.width / 2,
                imgY + imgHeight + 2,
                { align: "center" },
              );
              doc.setFontSize(10);
            }
          }
        }
      }
    },

    didDrawPage: (data) => {
      addHeader(doc, 10, doc.tipoMantenimiento);
      addFooter(doc);
    },
  });

  return doc.lastAutoTable.finalY + 10;
};

// ✅ Función para renderizar checklist en layout alternativo (items en filas, máquinas en columnas)
export const renderChecklistRowLayout = async (doc, seccion, report, y) => {
  const maxWidth = doc.internal.pageSize.getWidth() - 30; // Ancho disponible en mm

  // En el layout de filas:
  // - Primera columna: nombres de los items
  // - Siguientes columnas: una por cada máquina
  const headers = [
    "Item",
    ...seccion.maquinas.map((maquina) => {
      if (maquina.tipo && maquina.numero) {
        return `${maquina.tipo} #${maquina.numero}`;
      }
      return (
        maquina.tipo || maquina.numero || maquina.descripcion || "Respuesta"
      );
    }),
  ];

  // Almacenar referencias a las celdas que contienen imágenes
  const imageCells = [];
  const imagePromises = [];
  const imageCache = {};

  // Mapear filas: una fila por cada item
  const rows = seccion.items.map((item, rowIndex) => {
    const row = [doc.splitTextToSize(item.descripcion, maxWidth)]; // Primera columna: descripción del item

    // Para cada máquina, buscar la respuesta de este item
    seccion.maquinas.forEach((maquina, colIndex) => {
      const maquinaItem = maquina.items.find((mItem) => mItem.id === item.id);

      if (!maquinaItem) {
        row.push("-");
        return;
      }

      if (maquinaItem.idtipoitem === 1) {
        switch (maquinaItem.idtiporespuesta) {
          case 1: // Booleano
            // Si tiene valor precargado, mostrar directamente la respuesta precargada
            if (maquinaItem.tienePrecarga && maquinaItem.valorPrecargado) {
              row.push(maquinaItem.valorPrecargado);
            } else if (maquinaItem.respuesta === null) {
              row.push("N/A");
            } else {
              row.push(maquinaItem.respuesta ? "OK" : "N/G");
            }
            break;
          case 2: // Numérico
            row.push(maquinaItem.respuesta || "-");
            break;
          case 3: // Texto
            row.push(maquinaItem.respuesta || "-");
            break;
          case 4: // Imagen
            try {
              if (maquinaItem.respuesta) {
                let imagePath, description;
                if (typeof maquinaItem.respuesta === "string") {
                  try {
                    const imageData = JSON.parse(maquinaItem.respuesta);
                    imagePath = imageData.valorimagen || imageData.path;
                    description =
                      imageData.descripcion || imageData.description || "";
                  } catch (e) {
                    imagePath = maquinaItem.respuesta;
                    description = "";
                  }
                } else if (
                  typeof maquinaItem.respuesta === "object" &&
                  maquinaItem.respuesta !== null
                ) {
                  imagePath =
                    maquinaItem.respuesta.valorimagen ||
                    maquinaItem.respuesta.path;
                  description =
                    maquinaItem.respuesta.descripcion ||
                    maquinaItem.respuesta.description ||
                    "";
                }
                if (imagePath) {
                  if (imagePath.startsWith('"') && imagePath.endsWith('"')) {
                    imagePath = imagePath.slice(1, -1);
                  }
                  // Registrar esta celda para agregar la imagen después
                  // colIndex + 1 porque la primera columna es la descripción del item
                  imageCells.push({
                    rowIndex,
                    colIndex: colIndex + 1,
                    imagePath,
                    description,
                  });
                  // Cargar la imagen si no está en caché
                  if (!imageCache[imagePath]) {
                    const promise = (async () => {
                      const imageUrl = await fetchImageUrl(imagePath);
                      if (imageUrl) {
                        imageCache[imagePath] = await convertToBase64(imageUrl);
                      }
                      return imageCache[imagePath];
                    })();
                    imagePromises.push(promise);
                  }
                  row.push("[Imagen]");
                } else {
                  row.push("Sin imagen");
                }
              } else {
                row.push("Sin imagen");
              }
            } catch (error) {
              console.error("Error procesando imagen:", error);
              row.push("Error imagen");
            }
            break;
          default:
            row.push("-");
        }
      } else if (maquinaItem.idtipoitem === 2) {
        // Replicar lógica de ChecklistSection.jsx para mostrar datos de consulta de la máquina
        const columnToAttributeMap = {
          "#": "numero",
          "# VÁLVULA": "numero",
          "# Válvula": "numero",
          Ubicación: "ubicacion",
          UBICACIÓN: "ubicacion",
          Tipo: "tipo",
          TIPO: "tipo",
          "Área Protegida": "areaprotegida",
          "ÁREA PROTEGIDA": "areaprotegida",
          Módulo: "modulo",
          MÓDULO: "modulo",
          Diámetro: "diametro",
          DIÁMETRO: "diametro",
          "Medida Nominal": "medidanominal",
          "MEDIDA NOMINAL": "medidanominal",
          "Matriz Causa y Efecto": "matrizcausaefecto",
          "MATRIZ CAUSA Y EFECTO": "matrizcausaefecto",
          Descripción: "descripcion",
          DESCRIPCIÓN: "descripcion",
          Descripcion: "descripcion",
          OBSERVACIONES: "observaciones",
          Observaciones: "observaciones",
        };

        // Normalizar la descripción del item para buscar en el mapa
        const descripcionNormalizada = maquinaItem.descripcion.trim();

        if (descripcionNormalizada.startsWith("#")) {
          row.push(String(maquina.numero ?? "-"));
          return;
        }

        // Caso especial para Ubicación: mostrar ubicacionespecifica
        if (
          descripcionNormalizada === "Ubicación" ||
          descripcionNormalizada === "UBICACIÓN" ||
          descripcionNormalizada === "Ubicacion"
        ) {
          row.push(maquina.ubicacionespecifica || maquina.ubicacion || "-");
        } else {
          const atributoConfigurado = report?.atributosMaquina?.find(
            (attr) => attr.idatributomaquina === maquinaItem.idatributomaquina,
          );
          if (atributoConfigurado?.keyatributo && maquina[atributoConfigurado.keyatributo] !== undefined) {
            row.push(String(maquina[atributoConfigurado.keyatributo] ?? "-"));
            return;
          }

          // Buscar el atributo correspondiente en el mapa
          const atributo = columnToAttributeMap[descripcionNormalizada];

          if (atributo && maquina[atributo]) {
            // Si el atributo existe en la máquina, retornarlo
            const valor = maquina[atributo];
            row.push(
              typeof valor === "string"
                ? capitalizeWords(valor)
                : String(valor),
            );
          } else {
            // Si no se encuentra en el mapa directo, intentar buscar case-insensitive
            const atributoKey = Object.keys(columnToAttributeMap).find(
              (key) =>
                key.toLowerCase() === descripcionNormalizada.toLowerCase(),
            );

            if (atributoKey) {
              const attr = columnToAttributeMap[atributoKey];
              if (maquina[attr]) {
                const valor = maquina[attr];
                row.push(
                  typeof valor === "string"
                    ? capitalizeWords(valor)
                    : String(valor),
                );
              } else {
                row.push("-");
              }
            } else {
              row.push("-");
            }
          }
        }
      } else {
        row.push("Sin resultado");
      }
    });

    return row;
  });

  // Esperar a que todas las imágenes se carguen
  await Promise.all(imagePromises);

  // Definir dimensiones máximas para las imágenes
  const maxImageWidth = 15; // mm (más pequeñas en el layout de filas)
  const maxImageHeight = 15; // mm

  // Calcular dimensiones proporcionales para cada celda de imagen
  for (const cell of imageCells) {
    if (imageCache[cell.imagePath]) {
      cell.dimensions = await getProportionalDimensions(
        imageCache[cell.imagePath],
        maxImageWidth,
        maxImageHeight,
      );
    }
  }

  // Precalcular las alturas de las filas según las imágenes que contienen
  const rowHeights = {};
  for (const cell of imageCells) {
    if (imageCache[cell.imagePath]) {
      const { height: imgHeight } = cell.dimensions || {
        height: maxImageHeight,
      };
      const totalCellHeight = imgHeight + (cell.description ? 10 : 3);

      if (
        !rowHeights[cell.rowIndex] ||
        totalCellHeight > rowHeights[cell.rowIndex]
      ) {
        rowHeights[cell.rowIndex] = totalCellHeight;
      }
    }
  }

  // Renderizar la tabla con el layout de filas
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: y,
    rowPageBreak: "avoid", // ✅ Evitar dividir filas entre páginas
    styles: {
      font: "helvetica",
      fontSize: 9, // Fuente más pequeña para acomodar más columnas
      cellPadding: 1.5,
      halign: "center",
      valign: "middle",
    },
    headStyles: {
      fillColor: [204, 0, 0],
      textColor: [255, 255, 255],
      fontSize: 9,
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: {
        // Primera columna (items)
        cellWidth: 60, // Ancho fijo para la columna de items
        halign: "left",
        fontSize: 8,
        fontStyle: "bold",
      },
    },
    margin: { top: 50, bottom: 25 },
    didParseCell: function (data) {
      // Aplicar altura personalizada a cada fila según las imágenes que contiene
      if (data.section === "body" && rowHeights[data.row.index]) {
        data.row.height = Math.max(data.row.height, rowHeights[data.row.index]);
      }
      // Colores intercalados para las filas
      if (data.section === "body") {
        if (data.row.index % 2 === 0) {
          data.cell.styles.fillColor = [255, 255, 255]; // Blanco
        } else {
          data.cell.styles.fillColor = [245, 245, 245]; // Gris claro
        }
      }
    },
    didDrawCell: (data) => {
      // Dibujar imágenes en las celdas correspondientes
      for (const cell of imageCells) {
        if (
          data.row.index === cell.rowIndex &&
          data.column.index === cell.colIndex &&
          data.section === "body"
        ) {
          const imageBase64 = imageCache[cell.imagePath];
          if (imageBase64) {
            const { width: imgWidth, height: imgHeight } = cell.dimensions || {
              width: maxImageWidth,
              height: maxImageHeight,
            };
            const imgX = data.cell.x + (data.cell.width - imgWidth) / 2;
            const imgY = data.cell.y + 1;
            doc.addImage(imageBase64, "JPEG", imgX, imgY, imgWidth, imgHeight);
            // Agregar descripción si existe
            if (cell.description) {
              const descriptionText =
                cell.description.length > 10
                  ? cell.description.substring(0, 10) + "..."
                  : cell.description;
              doc.setFontSize(6);
              doc.text(
                descriptionText,
                data.cell.x + data.cell.width / 2,
                imgY + imgHeight + 1,
                { align: "center" },
              );
              doc.setFontSize(9);
            }
          }
        }
      }
    },
    didDrawPage: (data) => {
      addHeader(doc, 10, doc.tipoMantenimiento);
      addFooter(doc);
    },
  });

  return doc.lastAutoTable.finalY + 10;
};
